/** UI + research locale for `/free-trial` (matches marketing `?lang=nl`). */
export type TrialLanguageLocale = 'nl' | 'en';

type LangQuery = {
  lang?: string | string[] | null;
};

/**
 * Dutch marketing links use `?lang=nl`. Without it the funnel stays English.
 */
export function resolveTrialLanguageLocale(query: LangQuery): TrialLanguageLocale {
  const raw = query.lang;
  const value = Array.isArray(raw) ? raw[0] : raw;

  if (typeof value === 'string' && value.trim().toLowerCase().startsWith('nl')) {
    return 'nl';
  }

  return 'en';
}

export function appLocaleFromDomain(domain: string): TrialLanguageLocale | null {
  return (domain.split('.').pop() ?? '').toLowerCase() === 'nl' ? 'nl' : null;
}

/** Locale sent to POST /v3/trial-research (`.nl` sites scan in Dutch). */
export function resolveResearchStartLocale(
  marketingLocale: TrialLanguageLocale,
  domain: string,
): TrialLanguageLocale {
  return appLocaleFromDomain(domain) ?? marketingLocale;
}

/**
 * Default “App language” on step 1: marketing Dutch wins; else API suggestion.
 */
export function resolveDefaultAppLanguage(
  marketingLocale: TrialLanguageLocale,
  suggested?: TrialLanguageLocale | null,
): TrialLanguageLocale {
  if (marketingLocale === 'nl') {
    return 'nl';
  }

  if (suggested === 'nl' || suggested === 'en') {
    return suggested;
  }

  return marketingLocale;
}

type TrialUiLocaleOptions = {
  domain?: string | null;
  suggested?: TrialLanguageLocale | null;
};

/**
 * Free-trial page i18n: marketing `?lang=nl`, then `.nl` domain, then API suggestion.
 */
export function resolveTrialUiLocale(
  marketingLocale: TrialLanguageLocale,
  options: TrialUiLocaleOptions = {},
): TrialLanguageLocale {
  if (marketingLocale === 'nl') {
    return 'nl';
  }

  const domain = options.domain?.trim();

  if (domain && appLocaleFromDomain(domain)) {
    return 'nl';
  }

  if (options.suggested === 'nl') {
    return 'nl';
  }

  return marketingLocale;
}
